import { Agent } from './agent'

/**
 * Shared display metadata for an agent.
 *
 * This catalog is the source used by backend/admin/frontend surfaces to avoid
 * duplicating labels, icons, launcher visibility, and grouping behavior.
 */
export interface Display {
  /** Describes how the frontend should expose this agent. */
  readonly assistantKind: Display.Kind

  /** Short user-facing explanation of what the agent does. */
  readonly description: string

  /** Shared group that this agent belongs to in assistant UI surfaces. */
  readonly groupId: Display.Group.Id

  /** Icon name used by frontend assistant selection UI. */
  readonly icon: string

  /** Canonical internal agent ID. */
  readonly id: Agent.Id

  /** User-facing agent label. */
  readonly name: string

  /** Sort order within catalogs and grouped assistant UI. */
  readonly order: number
}

export namespace Display {
  /**
   * Controls whether an agent is rendered by generic chat UI, reserved for
   * custom frontend behavior, or hidden from launcher/admin assignment lists.
   */
  export enum Kind {
    Custom = 'custom',
    GenericChat = 'genericChat',
    Hidden = 'hidden',
  }

  /**
   * Derived assistant group metadata.
   *
   * Groups collect one or more agents into a shared conversational surface.
   */
  export interface Group {
    /** Agents that belong to this group, already sorted by display order. */
    readonly agents: readonly Display[]

    /** Group visibility/behavior, inherited from the first ordered agent. */
    readonly assistantKind: Kind

    /** Stable shared group identifier. */
    readonly id: Group.Id

    /** Sort order for the group, inherited from the first ordered agent. */
    readonly order: number
  }

  export namespace Group {
    /**
     * Stable identifiers for assistant display groups.
     *
     * These are frontend grouping keys, not persisted agent IDs.
     */
    export enum Id {
      AttainIqExperimental = 'AttainIqExperimental',
      Bch = 'Bch',
      ChangeImpactAnalysis = 'ChangeImpactAnalysis',
      DeckOrchestrator = 'DeckOrchestrator',
      Dms = 'Dms',
      DmsExcelGenerator = 'DmsExcelGenerator',
      DmsQueryGenerator = 'DmsQueryGenerator',
      DmsSchemaAssistant = 'DmsSchemaAssistant',
      DiscoveryInsightsAnalyzer = 'DiscoveryInsightsAnalyzer',
      DolomiteGeneral = 'DolomiteGeneral',
      EmployeeUtilizationAssistant = 'EmployeeUtilizationAssistant',
      EffortChecker = 'EffortChecker',
      EngagementCharterGenerator = 'EngagementCharterGenerator',
      Gil = 'Gil',
      Guide = 'Guide',
      Migration = 'migration',
      Mosaic = 'Mosaic',
      MosaicTreeEditor = 'MosaicTreeEditor',
      Naming = 'Naming',
      OcmAscendKnowledgeBaseAssistant = 'OcmAscendKnowledgeBaseAssistant',
      OcmAscendTranscriptAnalyzer = 'OcmAscendTranscriptAnalyzer',
      OcmChangeManagementKnowledgeBaseAssistant = 'OcmChangeManagementKnowledgeBaseAssistant',
      PolicyAssistant = 'PolicyAssistant',
      PptGenerator = 'PPTGenerator',
      ResearchPerformanceAnalysis = 'ResearchPerformanceAnalysis',
      PrincetonAscendArchitect = 'PrincetonAscendArchitect',
      ProjectPlanGenerator = 'ProjectPlanGenerator',
      ProposalGenerator = 'ProposalGenerator',
      ProposalMatrixGenerator = 'ProposalMatrixGenerator',
      StakeholderAnalysis = 'StakeholderAnalysis',
      WorkPacketGenerator = 'WorkPacketGenerator',
    }
  }
}

/**
 * Complete display metadata catalog for every known agent.
 */
const ALL_DISPLAYS: Record<Agent.Id, Display> = {
  [Agent.Id.OcmAscendTranscriptAnalyzer]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Extracts OCM Ascend change impacts from meeting transcripts and returns a DMS-ready workbook',
    groupId: Display.Group.Id.OcmAscendTranscriptAnalyzer,
    icon: 'font',
    id: Agent.Id.OcmAscendTranscriptAnalyzer,
    name: 'OCM Ascend Transcript Analyzer',
    order: 10,
  },
  [Agent.Id.ChangeImpactAnalysis]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'Assists with analysis of Change Impacts in the DMS',
    groupId: Display.Group.Id.ChangeImpactAnalysis,
    icon: 'font',
    id: Agent.Id.ChangeImpactAnalysis,
    name: 'Change Impact Analyst',
    order: 20,
  },
  [Agent.Id.StakeholderAnalysis]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'Assists with analysis of Stakeholders in the DMS',
    groupId: Display.Group.Id.StakeholderAnalysis,
    icon: 'font',
    id: Agent.Id.StakeholderAnalysis,
    name: 'Stakeholder Analyst',
    order: 22,
  },
  [Agent.Id.ResearchPerformanceAnalysis]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Assists with analysis of Research Performance data in the DMS',
    groupId: Display.Group.Id.ResearchPerformanceAnalysis,
    icon: 'font',
    id: Agent.Id.ResearchPerformanceAnalysis,
    name: 'Research Performance Analyst',
    order: 21,
  },
  [Agent.Id.DmsExcelGenerator]: {
    assistantKind: Display.Kind.Hidden,
    description:
      'Generates a Structure Excel file from DMS entities and transforms uploaded data into the required format.',
    groupId: Display.Group.Id.DmsExcelGenerator,
    icon: 'dms',
    id: Agent.Id.DmsExcelGenerator,
    name: 'DMS Excel Generator',
    order: 30,
  },
  [Agent.Id.DeckOrchestrator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Turns a brief into a finished deck by scoping the narrative and handing the build to the PPT Generator',
    groupId: Display.Group.Id.DeckOrchestrator,
    icon: 'font',
    id: Agent.Id.DeckOrchestrator,
    name: 'Deck Orchestrator',
    order: 36,
  },
  [Agent.Id.Dms]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Answers questions about your data models and writes the queries that answer questions about your data',
    groupId: Display.Group.Id.Dms,
    icon: 'dms',
    id: Agent.Id.Dms,
    name: 'DMS Assistant',
    order: 35,
  },
  [Agent.Id.DmsQueryGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'Generates DMS queries based on your schema',
    groupId: Display.Group.Id.DmsQueryGenerator,
    icon: 'dms',
    id: Agent.Id.DmsQueryGenerator,
    name: 'DMS Query Generator',
    order: 40,
  },
  [Agent.Id.DmsSchemaAssistant]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'Assists with understanding and navigating the DMS schema',
    groupId: Display.Group.Id.DmsSchemaAssistant,
    icon: 'dms',
    id: Agent.Id.DmsSchemaAssistant,
    name: 'DMS Schema Assistant',
    order: 50,
  },
  [Agent.Id.DiscoveryInsightsAnalyzer]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Analyzes discovery materials to identify themes, insights, and opportunities',
    groupId: Display.Group.Id.DiscoveryInsightsAnalyzer,
    icon: 'font',
    id: Agent.Id.DiscoveryInsightsAnalyzer,
    name: 'Discovery Insights Analyzer',
    order: 52,
  },
  [Agent.Id.DolomiteGeneral]: {
    assistantKind: Display.Kind.Hidden,
    description:
      "An enterprise Salesforce architecture assistant specializing in the Ascend managed package and Princeton's implementation ecosystem.",
    groupId: Display.Group.Id.DolomiteGeneral,
    icon: 'font',
    id: Agent.Id.DolomiteGeneral,
    name: 'Attain IQ General',
    order: 55,
  },
  [Agent.Id.AttainIqExperimental]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'An experimental version of Attain IQ General that uses a revised instruction set.',
    groupId: Display.Group.Id.AttainIqExperimental,
    icon: 'font',
    id: Agent.Id.AttainIqExperimental,
    name: 'Attain IQ Experimental',
    order: 56,
  },
  [Agent.Id.Guide]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Helps you discover which AI assistants you have access to and what each one does',
    groupId: Display.Group.Id.Guide,
    icon: 'compass',
    id: Agent.Id.Guide,
    name: 'Guide',
    order: 60,
  },
  [Agent.Id.Gil]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Provides practical guidance on indirect costs, federal award cost principles, research administration, and sponsor-specific grant policy',
    groupId: Display.Group.Id.Gil,
    icon: 'font',
    id: Agent.Id.Gil,
    name: 'Gil',
    order: 65,
  },
  [Agent.Id.Mosaic]: {
    assistantKind: Display.Kind.Hidden,
    description: 'Answers questions about your Mosaic data and reports',
    groupId: Display.Group.Id.Mosaic,
    icon: 'mosaic',
    id: Agent.Id.Mosaic,
    name: 'Mosaic Assistant',
    order: 70,
  },
  [Agent.Id.Naming]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Helps you find the perfect name for concepts, features, or ideas',
    groupId: Display.Group.Id.Naming,
    icon: 'font',
    id: Agent.Id.Naming,
    name: 'Naming Assistant',
    order: 80,
  },
  [Agent.Id.OcmAscendKnowledgeBaseAssistant]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Supports consultants constructing training material from Ascend materials',
    groupId: Display.Group.Id.OcmAscendKnowledgeBaseAssistant,
    icon: 'font',
    id: Agent.Id.OcmAscendKnowledgeBaseAssistant,
    name: 'OCM Ascend Knowledge Base Assistant',
    order: 90,
  },
  [Agent.Id.OcmChangeManagementKnowledgeBaseAssistant]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Helps users understand, apply, and compare change management concepts, deliverables, frameworks, and terminology',
    groupId: Display.Group.Id.OcmChangeManagementKnowledgeBaseAssistant,
    icon: 'font',
    id: Agent.Id.OcmChangeManagementKnowledgeBaseAssistant,
    name: 'OCM Knowledge Base',
    order: 205,
  },
  [Agent.Id.PrincetonAscendArchitect]: {
    assistantKind: Display.Kind.Hidden,
    description:
      "An enterprise Salesforce architecture assistant specializing in the Ascend managed package and Princeton's implementation ecosystem.",
    groupId: Display.Group.Id.PrincetonAscendArchitect,
    icon: 'font',
    id: Agent.Id.PrincetonAscendArchitect,
    name: 'Dolomite',
    order: 95,
  },
  [Agent.Id.OrgChart]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'Helps you visualize and manage organizational structures',
    groupId: Display.Group.Id.MosaicTreeEditor,
    icon: 'cluster',
    id: Agent.Id.OrgChart,
    name: 'Organization Chart Assistant',
    order: 100,
  },
  [Agent.Id.ProjectRecommendation]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Suggests optimal project assignments based on skills and availability',
    groupId: Display.Group.Id.MosaicTreeEditor,
    icon: 'sun',
    id: Agent.Id.ProjectRecommendation,
    name: 'Project Recommendation Assistant',
    order: 110,
  },
  [Agent.Id.EmployeeUtilizationAssistant]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Analyzes employee utilization, planned capacity, availability, and over-allocation',
    groupId: Display.Group.Id.EmployeeUtilizationAssistant,
    icon: 'font',
    id: Agent.Id.EmployeeUtilizationAssistant,
    name: 'Employee Utilization Assistant',
    order: 115,
  },
  [Agent.Id.EffortChecker]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'Checks model reasoning effort behavior',
    groupId: Display.Group.Id.EffortChecker,
    icon: 'font',
    id: Agent.Id.EffortChecker,
    name: 'Effort Checker',
    order: 117,
  },
  [Agent.Id.ProposalMatrixGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Assists with proposal writing, review, and compliance matrix generation',
    groupId: Display.Group.Id.ProposalMatrixGenerator,
    icon: 'font',
    id: Agent.Id.ProposalMatrixGenerator,
    name: 'Proposal Matrix Generator',
    order: 120,
  },
  [Agent.Id.Stm]: {
    assistantKind: Display.Kind.Hidden,
    description: 'Agent for automated ETL source-to-target mapping generation',
    groupId: Display.Group.Id.Migration,
    icon: 'rule',
    id: Agent.Id.Stm,
    name: 'STM',
    order: 130,
  },
  [Agent.Id.StmUnitTesting]: {
    assistantKind: Display.Kind.Hidden,
    description: 'Validates generated source-to-target mappings.',
    groupId: Display.Group.Id.Migration,
    icon: 'approve',
    id: Agent.Id.StmUnitTesting,
    name: 'STM Unit Testing',
    order: 140,
  },
  [Agent.Id.PptGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Builds Attain-branded decks from an outline and a master deck you attach',
    groupId: Display.Group.Id.PptGenerator,
    icon: 'font',
    id: Agent.Id.PptGenerator,
    name: 'PPT Generator',
    order: 150,
  },
  [Agent.Id.Bch]: {
    assistantKind: Display.Kind.GenericChat,
    description: 'BCH assistant.',
    groupId: Display.Group.Id.Bch,
    icon: 'rp',
    id: Agent.Id.Bch,
    name: 'BCH Assistant',
    order: 160,
  },
  [Agent.Id.WorkPacketGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Generates a self-contained work packet for a single software requirement',
    groupId: Display.Group.Id.WorkPacketGenerator,
    icon: 'font',
    id: Agent.Id.WorkPacketGenerator,
    name: 'Work Packet Generator',
    order: 170,
  },
  [Agent.Id.ProposalGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Generates complete first-draft Word proposals from RFPs and supporting materials',
    groupId: Display.Group.Id.ProposalGenerator,
    icon: 'font',
    id: Agent.Id.ProposalGenerator,
    name: 'Proposal Generator',
    order: 180,
  },
  [Agent.Id.EngagementCharterGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Generates a first-draft Engagement Charter for a project from its filed BD notes, RFP, SOW, and other source documents',
    groupId: Display.Group.Id.EngagementCharterGenerator,
    icon: 'font',
    id: Agent.Id.EngagementCharterGenerator,
    name: 'Engagement Charter Generator',
    order: 190,
  },
  [Agent.Id.ProjectPlanGenerator]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Generates an Excel project plan for a project from its requirements, proposed dates, and dependencies',
    groupId: Display.Group.Id.ProjectPlanGenerator,
    icon: 'font',
    id: Agent.Id.ProjectPlanGenerator,
    name: 'Project Plan Generator',
    order: 200,
  },
  [Agent.Id.PolicyAssistant]: {
    assistantKind: Display.Kind.GenericChat,
    description:
      'Answers questions about Attain Partners company policies from the policy documents',
    groupId: Display.Group.Id.PolicyAssistant,
    icon: 'book',
    id: Agent.Id.PolicyAssistant,
    name: 'Policy Assistant',
    order: 210,
  },
}

export namespace Display {
  /**
   * Complete display metadata catalog for every known agent.
   */
  export const ALL: readonly Display[] = Object.values(ALL_DISPLAYS)

  /**
   * Looks up display metadata for a canonical agent ID.
   */
  export function get(agentId: string): Display | undefined {
    for (const agent of ALL) {
      if (agent.id === agentId) {
        return agent
      }
    }

    return undefined
  }

  /**
   * Looks up display metadata and fails fast when the agent is not cataloged.
   */
  export function assertGet(agentId: string): Display {
    const display = get(agentId)

    if (!display) {
      throw new Error(`Agent display metadata was not found for "${agentId}".`)
    }

    return display
  }
}

/**
 * Builds a group from already-related agents.
 */
function createGroup(agents: readonly Display[]): Display.Group {
  const sortedAgents = [...agents].sort((a, b) => a.order - b.order)
  const [firstAgent] = sortedAgents as [Display, ...Display[]]

  return {
    agents: sortedAgents,
    assistantKind: firstAgent.assistantKind,
    id: firstAgent.groupId,
    order: firstAgent.order,
  }
}

/**
 * Derives groups from agent-level display metadata.
 */
function createGroups(displays: readonly Display[]): readonly Display.Group[] {
  const groupsById = new Map<Display.Group.Id, Display[]>()

  for (const display of displays) {
    // Multiple agents can intentionally share a group, such as MosaicTreeEditor.
    const group = groupsById.get(display.groupId) ?? []
    group.push(display)
    groupsById.set(display.groupId, group)
  }

  return Array.from(groupsById.values())
    .map(createGroup)
    .sort((a, b) => a.order - b.order)
}

const ALL_GROUPS = createGroups(Display.ALL)
const GROUPS_BY_ID = new Map(ALL_GROUPS.map(group => [group.id, group]))
const GROUPS_BY_AGENT_ID = new Map<string, Display.Group>(
  ALL_GROUPS.flatMap(group =>
    group.agents.map(agent => [agent.id, group] as const),
  ),
)

export namespace Display {
  export namespace Group {
    /**
     * Complete derived group catalog.
     */
    export const ALL: readonly Group[] = ALL_GROUPS

    /**
     * Looks up a display group by its stable group ID.
     */
    export function get(groupId: Id): Group | undefined {
      return GROUPS_BY_ID.get(groupId)
    }

    /**
     * Looks up the display group that owns a given agent.
     */
    export function getByAgentId(agentId: string): Group | undefined {
      return GROUPS_BY_AGENT_ID.get(agentId)
    }

    /**
     * Lists groups that can be shown in assignment/catalog UI.
     */
    export function getAssignable(): readonly Group[] {
      return ALL.filter(group => group.assistantKind !== Kind.Hidden)
    }

    /**
     * Builds visible groups from a user's assigned agent IDs.
     *
     * Returned groups keep only the assigned agents within each visible group.
     */
    export function getVisibleForAgentIds(
      agentIds: Iterable<string>,
    ): readonly Group[] {
      const assignedAgentIds = new Set(agentIds)

      return ALL.flatMap(group => {
        if (group.assistantKind === Kind.Hidden) {
          return []
        }

        const agents = group.agents.filter(agent =>
          assignedAgentIds.has(agent.id),
        )

        return agents.length ? [createGroup(agents)] : []
      })
    }
  }
}
